import React, { useState, useEffect, useRef, useCallback } from 'react';
import requestService from '../services/requestService';
import { useNotification } from '../context/NotificationContext';
import RequestDetailsModal from './RequestDetailsModal';

function AdminRequestsTab() {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all'); // all, pending, approved, rejected
  const { showNotification } = useNotification();
  
  // Pagination state
  const [pagination, setPagination] = useState({
    total: 0,
    page: 1,
    limit: 10, 
    totalPages: 1
  });
  
  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [detailedRequest, setDetailedRequest] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  
  // Rejection modal state
  const [rejectionModalOpen, setRejectionModalOpen] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const requestToReject = useRef(null);
  
  // Helper function to load demo data for development
  const loadDemoData = useCallback(() => {
    // Use the demo request data from the service
    const demoData = requestService.getDemoRequests(filter !== 'all' ? filter : null);
    
    if (demoData && demoData.requests) {
      setRequests(demoData.requests);
      setPagination({
        total: demoData.pagination?.total || demoData.requests.length,
        page: demoData.pagination?.page || 1,
        limit: demoData.pagination?.limit || 10,
        totalPages: demoData.pagination?.totalPages || Math.ceil(demoData.requests.length / 10)
      });
    } else {
      // Fallback to hardcoded data if the service method failed
      const fallbackRequests = [
        {
          id: 'req_001',
          listingId: 'list_001',
          listing: {
            title: 'Fresh Vegetables Assortment',
            category: 'Produce',
            quantity: '15kg',
            expiry: '2023-08-20'
          },
          donor: {
            id: 'donor_001',
            name: 'FreshGreens Market',
            location: 'Nairobi Central'
          },
          recipient: {
            id: 'recip_001',
            name: 'Hope Children Home',
            organization: 'Hope Foundation'
          },
          quantity: '10kg',
          status: 'pending',
          createdAt: '2023-08-12T10:30:00Z',
          notes: 'Weekend pickup preferred'
        },
        {
          id: 'req_002',
          listingId: 'list_002',
          listing: {
            title: 'Bread and Pastries',
            category: 'Bakery',
            quantity: '25 items',
            expiry: '2023-08-15'
          },
          donor: {
            id: 'donor_002',
            name: 'Daily Bake Shop',
            location: 'Westlands'
          },
          recipient: {
            id: 'recip_002',
            name: 'Street Families Rescue',
            organization: 'Urban Relief NGO'
          },
          quantity: '25 items',
          status: 'approved',
          createdAt: '2023-08-11T08:20:00Z',
          pickupTime: '2023-08-14T10:00:00Z',
          notes: 'Will collect early morning'
        },
        {
          id: 'req_003',
          listingId: 'list_003',
          listing: {
            title: 'Rice and Beans Surplus',
            category: 'Dry Goods',
            quantity: '50kg',
            expiry: '2024-01-15'
          },
          donor: {
            id: 'donor_003',
            name: 'Community Pantry',
            location: 'Embakasi'
          },
          recipient: {
            id: 'recip_003',
            name: 'City Shelter',
            organization: 'City Support Initiative'
          },
          quantity: '30kg',
          status: 'rejected',
          createdAt: '2023-08-10T14:45:00Z',
          rejectionReason: 'Request quantity exceeds policy limits'
        }
      ];
      
      setRequests(fallbackRequests);
      setPagination({
        total: 3,
        page: 1,
        limit: 10,
        totalPages: 1
      });
    }
  }, [filter]);

  const fetchRequests = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Use the filter parameter and pagination for the API call
      const params = {
        page: pagination.page,
        limit: pagination.limit
      };
      
      if (filter !== 'all') {
        params.status = filter;
      }
      
      console.log('Fetching admin requests with params:', params);
      try {
        const response = await requestService.getAdminRequests(params);
        console.log('Admin requests response:', response);
        
        // Handle different response formats
        let requestsData = [];
        let paginationData = {};
        
        console.log('Analyzing admin requests response structure:', response);
        
        // Check for the nested structure we've identified in the API responses
        if (response?.requests?.results && Array.isArray(response.requests.results)) {
          console.log('Detected nested requests.results format');
          requestsData = response.requests.results;
          paginationData = response.pagination || response.requests.pagination || {};
        }
        // Check if response has Cloudflare D1 format with results property
        else if (response && response.results) {
          console.log('Detected Cloudflare D1 results format');
          requestsData = response.results;
          paginationData = response.pagination || {};
        } 
        // Check if response has requests array directly
        else if (response && Array.isArray(response.requests)) {
          console.log('Detected standard requests format');
          requestsData = response.requests;
          paginationData = response.pagination || {};
        }
        // Check if response has non-array requests property and pagination
        else if (response?.requests && typeof response.requests === 'object' && response.pagination?.total > 0) {
          console.log('Found pagination with total > 0 but requests is not an array, using demo data');
          showNotification('API returned inconsistent data. Using demo data for development.', 'warning');
          return loadDemoData();
        }
        // Check for any deeply nested arrays in the response
        else if (response && typeof response === 'object') {
          console.log('Searching for nested arrays in response');
          // Deep search for an array containing request objects
          const findArrayInObject = (obj, depth = 0) => {
            if (depth > 3) return null; // Limit recursion depth
            
            for (const key in obj) {
              if (Array.isArray(obj[key]) && obj[key].length > 0) {
                // Check if this looks like a request array (contains objects with id, status fields)
                if (obj[key][0] && obj[key][0].id && obj[key][0].status) {
                  console.log(`Found likely request array at key: ${key}`);
                  return obj[key];
                }
              } else if (typeof obj[key] === 'object' && obj[key] !== null) {
                const result = findArrayInObject(obj[key], depth + 1);
                if (result) return result;
              }
            }
            return null;
          };
          
          const foundArray = findArrayInObject(response);
          if (foundArray) {
            console.log('Found request array through deep search:', foundArray);
            requestsData = foundArray;
          } else {
            console.warn('Could not find request array in response through deep search');
          }
          
          // Handle the case where the API claims there are records (pagination.total > 0)
          // but returns an empty array - use demo data in this case
          const paginationTotal = response.pagination?.total || 0;
          if (requestsData.length === 0 && paginationTotal > 0) {
            console.warn('API returned empty array but pagination shows total:', paginationTotal);
            showNotification('API returned empty data but indicated records exist. Using demo data.', 'warning');
            return loadDemoData();
          }
        } 
        // Check if response is just an array
        else if (Array.isArray(response)) {
          console.log('Detected array response format');
          requestsData = response;
        }
        // Handle case where no valid data is found
        else {
          console.warn('API returned unknown response format:', response);
          showNotification('Received data format not recognized. Using fallback data.', 'warning');
          // If we have no valid data format, use demo data
          return loadDemoData();
        }
        
        // If we got to this point, we have some valid data to display
        console.log('Setting requests data:', requestsData);
        setRequests(requestsData);
        
        // Update pagination if available
        setPagination({
          total: paginationData.total || requestsData.length || 0,
          page: paginationData.page || pagination.page,
          limit: paginationData.limit || pagination.limit,
          totalPages: paginationData.totalPages || Math.ceil((paginationData.total || requestsData.length) / pagination.limit) || 1
        });
        
      } catch (apiError) {
        console.error('API error when fetching requests:', apiError);
        showNotification('Error loading requests. Using demo data for development.', 'error');
        loadDemoData();
      }
    } catch (error) {
      console.error('Error in fetchRequests:', error);
      setError('Failed to load requests. Using demo data instead.');
      loadDemoData();
    } finally {
      setLoading(false);
    }
  }, [filter, pagination.page, pagination.limit, showNotification, loadDemoData]);

  useEffect(() => {
    fetchRequests();
  }, [fetchRequests]); // Re-fetch when fetchRequests changes

  const fetchRequestDetails = useCallback(async (requestId) => {
    setLoadingDetails(true);
    try {
      console.log(`Fetching details for request ID: ${requestId}`);
      const response = await requestService.getAdminRequestById(requestId);
      console.log('Request details response:', response);
      
      // Handle different response formats
      let requestData = null;
      
      if (response && response.request) {
        // Standard format with request directly in the response
        requestData = response.request;
      } else if (response && response.results && response.results[0]) {
        // D1 database format with results array
        requestData = response.results[0];
      } else if (response && !response.message) {
        // The response might be the request object itself
        requestData = response;
      }
      
      if (requestData) {
        console.log('Setting detailed request data:', requestData);
        setDetailedRequest(requestData);
      } else {
        console.warn('No valid request details found in response');
        showNotification('Could not retrieve complete request details', 'warning');
      }
    } catch (error) {
      console.error('Error fetching request details:', error);
      showNotification('Failed to fetch request details', 'error');
      // Keep using the basic request data we already have
    } finally {
      setLoadingDetails(false);
    }
  }, [showNotification]);
  
  const handleViewRequest = (request) => {
    setSelectedRequest(request);
    setDetailedRequest(null); // Clear previous details
    setModalOpen(true);
    fetchRequestDetails(request.id);
  };
  
  const handleApproveRequest = async (requestId) => {
    try {
      console.log(`Approving request ID: ${requestId}`);
      const response = await requestService.adminApproveRequest(requestId);
      console.log('Approval response:', response);
      showNotification(response?.message || 'Request approved successfully', 'success');
      fetchRequests(); // Refresh the list
    } catch (error) {
      console.error('Error approving request:', error);
      showNotification(error?.message || 'Failed to approve request', 'error');
    }
  };
  
  const handleOpenRejectModal = (requestId) => {
    requestToReject.current = requestId;
    setRejectionReason('');
    setRejectionModalOpen(true);
  };
  
  const handleRejectRequest = async () => {
    if (!requestToReject.current) return;
    
    try {
      console.log(`Rejecting request ID: ${requestToReject.current} with reason:`, rejectionReason);
      const response = await requestService.adminRejectRequest(
        requestToReject.current, 
        { rejection_reason: rejectionReason }
      );
      console.log('Rejection response:', response);
      showNotification(response?.message || 'Request rejected successfully', 'success');
      setRejectionModalOpen(false);
      setRejectionReason('');
      fetchRequests(); // Refresh the list
    } catch (error) {
      console.error('Error rejecting request:', error);
      showNotification(error?.message || 'Failed to reject request', 'error');
    } finally {
      requestToReject.current = null;
    }
  };
  
  // Pagination handlers
  const handlePageChange = (newPage) => {
    if (newPage < 1 || newPage > pagination.totalPages) return;
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  // Filter requests based on status
  const filteredRequests = filter === 'all' 
    ? requests 
    : requests.filter(request => request.status === filter);

  return (
    <div className="bg-white shadow rounded-lg overflow-hidden">
      <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
        <h3 className="text-lg leading-6 font-medium text-gray-900">Food Donation Requests</h3>
        <p className="mt-1 max-w-2xl text-sm text-gray-500">
          Manage and review food donation requests between donors and recipients
        </p>
      </div>
      
      {/* Filter tabs */}
      <div className="border-b border-gray-200">
        <div className="px-4 sm:px-6">
          <div className="flex space-x-4 mt-4">
            <button
              onClick={() => setFilter('all')}
              className={`px-3 py-2 text-sm font-medium rounded-md ${
                filter === 'all' 
                  ? 'bg-primary-100 text-primary-700' 
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              All Requests
            </button>
            <button
              onClick={() => setFilter('pending')}
              className={`px-3 py-2 text-sm font-medium rounded-md ${
                filter === 'pending' 
                  ? 'bg-yellow-100 text-yellow-700' 
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              Pending
            </button>
            <button
              onClick={() => setFilter('approved')}
              className={`px-3 py-2 text-sm font-medium rounded-md ${
                filter === 'approved' 
                  ? 'bg-green-100 text-green-700' 
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              Approved
            </button>
            <button
              onClick={() => setFilter('rejected')}
              className={`px-3 py-2 text-sm font-medium rounded-md ${
                filter === 'rejected' 
                  ? 'bg-red-100 text-red-700' 
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              Rejected
            </button>
          </div>
        </div>
      </div>
      
      <div className="px-4 py-5 sm:p-6">
        {/* Loading state */}
        {loading && (
          <div className="text-center py-10">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-500 mx-auto"></div>
            <p className="mt-2 text-sm text-gray-500">Loading requests...</p>
          </div>
        )}
        
        {/* Error state */}
        {error && (
          <div className="text-center py-10">
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100">
              <svg className="h-6 w-6 text-red-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <p className="mt-2 text-sm text-red-600">{error}</p>
          </div>
        )}
        
        {/* Empty state */}
        {!loading && !error && filteredRequests.length === 0 && (
          <div className="text-center py-10">
            <p className="text-sm text-gray-500">No requests found matching the selected filter.</p>
          </div>
        )}
        
        {/* Requests table */}
        {!loading && !error && filteredRequests.length > 0 && (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-300">
              <thead>
                <tr>
                  <th scope="col" className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-0">Request ID</th>
                  <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Food Item</th>
                  <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Donor</th>
                  <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Recipient</th>
                  <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Status</th>
                  <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Date</th>
                  <th scope="col" className="relative py-3.5 pl-3 pr-4 sm:pr-0">
                    <span className="sr-only">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredRequests.map((request) => (
                  <tr key={request.id}>
                    <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-0">
                      {request.id}
                    </td>
                    <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                      <div className="font-medium text-gray-900">{request.listing.title}</div>
                      <div className="text-xs text-gray-500">Qty: {request.quantity}</div>
                    </td>
                    <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                      {request.donor.name}
                    </td>
                    <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                      {request.recipient.name}
                    </td>
                    <td className="whitespace-nowrap px-3 py-4 text-sm">
                      <span className={`inline-flex rounded-full px-2 text-xs font-semibold leading-5 
                        ${request.status === 'pending' ? 'bg-yellow-100 text-yellow-800' : 
                          request.status === 'approved' ? 'bg-green-100 text-green-800' : 
                          'bg-red-100 text-red-800'}`}>
                        {request.status}
                      </span>
                    </td>
                    <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                      {new Date(request.createdAt).toLocaleDateString()}
                    </td>
                    <td className="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-0">
                       <button 
                        className="text-primary-600 hover:text-primary-900 mr-3"
                        onClick={() => handleViewRequest(request)}
                      >
                        View
                      </button>
                      
                      {request.status === 'pending' && (
                        <>
                          <button 
                            className="text-green-600 hover:text-green-900 mr-3"
                            onClick={() => handleApproveRequest(request.id)}
                          >
                            Approve
                          </button>
                          <button 
                            className="text-red-600 hover:text-red-900"
                            onClick={() => handleOpenRejectModal(request.id)}
                          >
                            Reject
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pagination controls */}
      {!loading && !error && requests.length > 0 && (
        <div className="flex items-center justify-between border-t border-gray-200 bg-white px-4 py-3 sm:px-6">
          <div className="flex flex-1 justify-between sm:hidden">
            <button
              onClick={() => handlePageChange(pagination.page - 1)}
              disabled={pagination.page === 1}
              className="relative inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Previous
            </button>
            <button
              onClick={() => handlePageChange(pagination.page + 1)}
              disabled={pagination.page === pagination.totalPages}
              className="relative ml-3 inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Next
            </button>
          </div>
          <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
            <p className="text-sm text-gray-700">
              Showing <span className="font-medium">{(pagination.page - 1) * pagination.limit + 1}</span> to{' '}
              <span className="font-medium">
                {Math.min(pagination.page * pagination.limit, pagination.total)}
              </span> of <span className="font-medium">{pagination.total}</span> results
            </p>
            <div>
              <nav className="isolate inline-flex -space-x-px rounded-md shadow-sm" aria-label="Pagination">
                <button
                  onClick={() => handlePageChange(pagination.page - 1)}
                  disabled={pagination.page === 1}
                  className={`relative inline-flex items-center rounded-l-md px-2 py-2 text-gray-400
                    ${pagination.page === 1 ? 'cursor-not-allowed' : 'hover:bg-gray-50'}`}
                >
                  <span className="sr-only">Previous</span>
                  <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                    <path fillRule="evenodd" d="M12.79 5.23a.75.75 0 01-.02 1.06L8.832 10 12.77 13.71a.75.75 0 11-1.04 1.08l-4.5-4.25a.75.75 0 010-1.08l4.5-4.25a.75.75 0 011.06.02z" clipRule="evenodd" />
                  </svg>
                </button>

                {/* Page number buttons - shows up to 5 pages at once */}
                {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                  // Calculate the page number to display (centered around current page if possible)
                  let pageNum;
                  if (pagination.totalPages <= 5) {
                    pageNum = i + 1; // Just show pages 1-5 if 5 or fewer total pages
                  } else if (pagination.page <= 3) {
                    pageNum = i + 1; // Show pages 1-5 when near the start
                  } else if (pagination.page >= pagination.totalPages - 2) {
                    // When near the end, show the last 5 pages
                    pageNum = pagination.totalPages - 4 + i;
                  } else {
                    // Otherwise center around the current page
                    pageNum = pagination.page - 2 + i;
                  }

                  return (
                    <button
                      key={pageNum}
                      onClick={() => handlePageChange(pageNum)}
                      aria-current={pagination.page === pageNum ? "page" : undefined}
                      className={`relative inline-flex items-center px-4 py-2 text-sm font-semibold
                        ${pagination.page === pageNum 
                          ? 'z-10 bg-primary-600 text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600' 
                          : 'text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:outline-offset-0'}`}
                    >
                      {pageNum}
                    </button>
                  );
                })}

                <button
                  onClick={() => handlePageChange(pagination.page + 1)}
                  disabled={pagination.page === pagination.totalPages}
                  className={`relative inline-flex items-center rounded-r-md px-2 py-2 text-gray-400
                    ${pagination.page === pagination.totalPages ? 'cursor-not-allowed' : 'hover:bg-gray-50'}`}
                >
                  <span className="sr-only">Next</span>
                  <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                    <path fillRule="evenodd" d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z" clipRule="evenodd" />
                  </svg>
                </button>
              </nav>
            </div>
          </div>
        </div>
      )}
      
      {/* Request details modal */}
      <RequestDetailsModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        request={loadingDetails ? selectedRequest : (detailedRequest || selectedRequest)}
      />
      
      {/* Rejection reason modal */}
      {rejectionModalOpen && (
        <div className="fixed inset-0 overflow-y-auto z-50">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div 
              className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" 
              onClick={() => setRejectionModalOpen(false)}
              aria-hidden="true"
            ></div>

            <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
              <div>
                <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
                  Reject Request
                </h3>
                <div className="mt-2">
                  <p className="text-sm text-gray-500 mb-4">
                    Please provide a reason for rejecting this request. This will be visible to both the donor and recipient.
                  </p>
                  <textarea
                    rows="4"
                    className="shadow-sm focus:ring-primary-500 focus:border-primary-500 block w-full sm:text-sm border-gray-300 rounded-md"
                    placeholder="Rejection reason..."
                    value={rejectionReason}
                    onChange={(e) => setRejectionReason(e.target.value)}
                  ></textarea>
                </div>
              </div>
              <div className="mt-5 sm:mt-6 sm:grid sm:grid-cols-2 sm:gap-3 sm:grid-flow-row-dense">
                <button
                  type="button"
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:col-start-2 sm:text-sm"
                  onClick={handleRejectRequest}
                >
                  Reject
                </button>
                <button
                  type="button"
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:mt-0 sm:col-start-1 sm:text-sm"
                  onClick={() => setRejectionModalOpen(false)}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminRequestsTab;
